import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Slider,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BUCKET_LABELS,
  checkReproduction,
  compareStructures,
  formatDays,
  formatMoney,
  formatPercent,
  fundingLabel,
  type CostBucket,
  type PricingResult,
  type StructureComparison,
} from './pricingEngine.model';
import { loadQuote, searchQuotes, type LoadedQuote, type QuoteSearchHit } from './pricingEngine.loader';

const BUCKETS: CostBucket[] = ['goods', 'company_flat_rate', 'direct_fitter', 'additional_trade', 'other_labour'];

const STRUCTURES = [
  {
    title: 'PS1 — Enshrined / Legacy',
    version: 'ps1.v1',
    body: 'The quote formula that wins work today, frozen. Company Flat Rate stays inside labour, and the quote screen’s profit is the admin fee, contingency and profit margin added together.',
  },
  {
    title: 'PS2 — Normalised Keystone Model',
    version: 'ps2.v0-draft',
    body: 'The same commercial result, restated. Direct fitter labour is separated from Company Flat Rate, and Company Flat Rate becomes central company overhead. James/Simon salary treatment is still TBC.',
  },
  {
    title: 'PS3 — Experimental / Market Model',
    version: 'ps3.v0-experimental',
    body: 'A future testing layer. A market/mood factor can raise or lower the required return when workload, demand or capacity changes. It is not used to issue quotes.',
  },
];

function percentLabel(ratio: number): string {
  return `${Math.round((ratio || 0) * 1000) / 10}%`;
}

function ReproductionBanner({ quote, comparison }: { quote: LoadedQuote; comparison: StructureComparison }) {
  const check = checkReproduction(comparison.ps1, {
    net: quote.storedNet,
    gross: quote.storedGross,
    deposit: quote.storedDeposit,
  }, quote.versionActive);
  const depositNote = quote.inputs.hasDeposit && check.depositDelta != null && Math.abs(check.depositDelta) >= 0.02
    ? ` The stored deposit differs by ${formatMoney(check.depositDelta)}.`
    : '';

  if (check.status === 'match') {
    return <Alert color="success">PS1 v1 reproduces the total stored on this quote.{depositNote}</Alert>;
  }
  if (check.status === 'differs') {
    const gap = check.grossDelta != null ? formatMoney(check.grossDelta) : formatMoney(check.netDelta);
    const source = quote.pricingSource === 'final'
      ? 'the Final Survey'
      : quote.pricingSource === 'first'
        ? 'the First Survey'
        : 'the selected version’s lines and fees';
    return (
      <Alert color="warning">
        PS1 v1 and the total last saved on this document differ by {gap}. This reading uses {source}.{depositNote}
      </Alert>
    );
  }
  if (check.status === 'inactive-version') {
    return (
      <Alert color="neutral">
        This is an earlier version. The total saved on the document belongs to the active version. The figures below price the version you selected.
      </Alert>
    );
  }
  return <Alert color="neutral">This quote has no stored total yet. The figures below are PS1 v1 applied to its lines.</Alert>;
}

function ComparisonTable({ comparison }: { comparison: StructureComparison }) {
  const columns = [comparison.ps1, comparison.ps2, comparison.ps3];
  const rows: Array<{ label: string; hint?: string; cell: (result: PricingResult) => string }> = [
    { label: 'Customer price (ex VAT)', cell: (result) => formatMoney(result.customerPriceExVat) },
    { label: 'Customer price (inc VAT)', cell: (result) => formatMoney(result.customerPriceIncVat) },
    { label: 'Goods / Outlay', cell: (result) => formatMoney(result.goodsOutlay) },
    { label: 'Direct Labour', cell: (result) => formatMoney(result.directLabour) },
    { label: 'Company Overhead', cell: (result) => formatMoney(result.companyOverhead) },
    { label: 'Contingency reserve', hint: 'Deducted before net profit in PS2 and PS3. Included inside the PS1 profit figure.', cell: (result) => formatMoney(result.contingency) },
    { label: 'Gross Profit', hint: 'Customer price ex VAT, minus Goods / Outlay and Direct Labour.', cell: (result) => formatMoney(result.grossProfit) },
    { label: 'Gross Margin', cell: (result) => formatPercent(result.grossMargin) },
    { label: 'Net Job Profit', hint: 'PS1 equals its gross profit. PS2 and PS3 deduct company overhead and contingency. James/Simon salary is not deducted.', cell: (result) => formatMoney(result.netJobProfit) },
    { label: 'Net Job Margin', cell: (result) => formatPercent(result.netJobMargin) },
    { label: 'Keystone days', cell: (result) => formatDays(result.keystoneDays) },
    { label: 'Profit per Keystone day', cell: (result) => formatMoney(result.profitPerKeystoneDay) },
    { label: 'Quote-screen profit', hint: 'Admin fee + contingency + profit margin, as the quote wizard shows it today.', cell: (result) => formatMoney(result.quoteScreenProfit) },
    { label: 'Quote-screen profit per day', cell: (result) => formatMoney(result.quoteScreenProfitPerDay) },
    { label: 'Customer deposit', cell: (result) => formatMoney(result.customerDeposit) },
    { label: 'Supplier funding requirement', hint: 'Cash payable on goods, including VAT.', cell: (result) => formatMoney(result.supplierFundingRequirement) },
    {
      label: 'Funding surplus / shortfall',
      hint: 'Customer deposit minus the supplier funding requirement.',
      cell: (result) => `${fundingLabel(result.fundingSurplus)} ${formatMoney(Math.abs(result.fundingSurplus))}`,
    },
  ];

  return (
    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
      <Table stickyHeader sx={{ minWidth: 760, '& td, & th': { whiteSpace: 'nowrap' } }}>
        <thead>
          <tr>
            <th style={{ width: '28%' }}>Output</th>
            {columns.map((column) => (
              <th key={column.structureId}>
                <Stack spacing={0.25}>
                  <span>{column.label}</span>
                  <Typography level="body-xs" textColor="neutral.500">{column.version}</Typography>
                </Stack>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td>
                <Typography level="body-sm" fontWeight="lg">{row.label}</Typography>
                {row.hint && (
                  <Typography level="body-xs" textColor="neutral.500" sx={{ whiteSpace: 'normal', maxWidth: 280 }}>
                    {row.hint}
                  </Typography>
                )}
              </td>
              {columns.map((column) => (
                <td key={column.structureId}>
                  <Typography level="body-sm">{row.cell(column)}</Typography>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}

const PricingEnginePage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const documentId = params.get('document') || '';
  const versionId = params.get('version') || '';
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<QuoteSearchHit[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [quote, setQuote] = useState<LoadedQuote | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, CostBucket>>({});
  const [mood, setMood] = useState(1);

  useEffect(() => {
    if (!documentId) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingQuote(true);
    setQuoteError(null);
    loadQuote(documentId, versionId || null)
      .then((loaded) => {
        if (!cancelled) setQuote(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(err instanceof Error ? err.message : 'Could not load that quote');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQuote(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, versionId]);

  useEffect(() => {
    setOverrides({});
    setMood(1);
  }, [quote?.documentId, quote?.versionId]);

  const comparison = useMemo(
    () => (quote ? compareStructures(quote.inputs, overrides, mood) : null),
    [quote, overrides, mood]
  );

  const onSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    if (term.trim().length < 2) {
      setHits([]);
      setHasSearched(false);
      setSearchError('Type at least two characters.');
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      setHits(await searchQuotes(term));
      setHasSearched(true);
    } catch (err: unknown) {
      setHits([]);
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const openHit = (hit: QuoteSearchHit) => {
    setParams({ document: hit.documentId });
  };

  const trace = comparison?.trace;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
      <Stack spacing={2.5}>
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography level="h3">Pricing Engine</Typography>
            <Chip size="sm" variant="soft" color="neutral">Analysis only</Chip>
          </Stack>
          <Typography level="body-sm" textColor="neutral.600" sx={{ maxWidth: 760 }}>
            A job keeps its own inputs — goods, labour, Keystone days, trades, deposit. Each pricing structure reads those inputs and produces its own result. Issued quotes stay on the structure and version that priced them. This workspace does not change live quote pricing.
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          {STRUCTURES.map((structure) => (
            <Card key={structure.version} variant="outlined" sx={{ flex: 1 }}>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography level="title-sm">{structure.title}</Typography>
                  <Chip size="sm" variant="outlined">{structure.version}</Chip>
                </Stack>
                <Typography level="body-xs" textColor="neutral.600">{structure.body}</Typography>
              </Stack>
            </Card>
          ))}
        </Stack>

        <Card variant="outlined">
          <form onSubmit={onSearch}>
            <Stack spacing={1.25}>
              <FormControl>
                <FormLabel>Open a real quote</FormLabel>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Input
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    placeholder="Customer, workspace, quote title or reference"
                    sx={{ flex: 1 }}
                  />
                  <Button type="submit" loading={searching}>Find quote</Button>
                </Stack>
              </FormControl>
              {searchError && <Alert color="danger">{searchError}</Alert>}
              {hits.length > 0 && (
                <Stack spacing={0.5} sx={{ maxHeight: 240, overflow: 'auto' }}>
                  {hits.map((hit) => (
                    <Button
                      key={hit.documentId}
                      variant={hit.documentId === documentId ? 'soft' : 'plain'}
                      color="neutral"
                      onClick={() => openHit(hit)}
                      sx={{ justifyContent: 'space-between', textAlign: 'left' }}
                    >
                      <Stack spacing={0}>
                        <Typography level="body-sm" fontWeight="lg">
                          {hit.lifecycleRef || hit.kind} · {hit.title}
                        </Typography>
                        <Typography level="body-xs" textColor="neutral.500">
                          {[hit.customerName, hit.projectName, hit.status].filter(Boolean).join(' · ')}
                        </Typography>
                      </Stack>
                      <Typography level="body-sm">{formatMoney(hit.grossTotal)}</Typography>
                    </Button>
                  ))}
                </Stack>
              )}
              {!searching && hasSearched && hits.length === 0 && !searchError && (
                <Typography level="body-sm" textColor="neutral.500">No estimates or quotations matched that search.</Typography>
              )}
            </Stack>
          </form>
        </Card>

        {loadingQuote && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {quoteError && <Alert color="danger">{quoteError}</Alert>}

        {quote && comparison && trace && (
          <>
            <Card variant="outlined">
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                  <Stack spacing={0.25}>
                    <Typography level="title-lg">{quote.title}</Typography>
                    <Typography level="body-sm" textColor="neutral.600">
                      {[quote.lifecycleRef, quote.customerName, quote.projectName, quote.status].filter(Boolean).join(' · ')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button component={Link} to={`/docs/${quote.documentId}`} variant="outlined" size="sm">Open quote</Button>
                    {quote.projectId && (
                      <Button component={Link} to={`/projects/${quote.projectId}/workspace`} variant="outlined" size="sm">Open workspace</Button>
                    )}
                  </Stack>
                </Stack>
                {quote.pricingSource === 'legacy' && quote.versions.length > 0 && (
                  <FormControl sx={{ maxWidth: 360 }}>
                    <FormLabel>Version</FormLabel>
                    <Select
                      value={quote.versionId || ''}
                      onChange={(_, value) => {
                        if (!value) return;
                        setParams({ document: quote.documentId, version: String(value) });
                      }}
                    >
                      {quote.versions.map((version) => (
                        <Option key={version.id} value={version.id}>
                          Version {version.number}{version.id === quote.liveVersionId ? ' · active' : ''}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {quote.pricingSource !== 'legacy' && (
                  <Typography level="title-sm">
                    {quote.pricingSource === 'final' ? 'Final Survey' : 'First Survey'}
                  </Typography>
                )}
                {quote.surveyUnpriced ? (
                  <Alert color="warning">
                    {quote.pricingSource === 'final' ? 'Final Survey' : 'First Survey'} has no pricing record on this quote yet.
                  </Alert>
                ) : (
                  <ReproductionBanner quote={quote} comparison={comparison} />
                )}
              </Stack>
            </Card>

            {!quote.surveyUnpriced && (
            <>
            <Card variant="outlined">
              <Stack spacing={1}>
                <Typography level="title-md">How PS1 priced this quote</Typography>
                <Typography level="body-xs" textColor="neutral.600">
                  {PS1_PARAMETER_LINE(quote)}
                </Typography>
                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                  <Stack spacing={0.4}>
                    <TraceRow label="Goods / Outlay (net)" value={formatMoney(trace.goodsNet)} />
                    <TraceRow label="Labour lines (net)" value={formatMoney(trace.labourNet)} />
                    <TraceRow label="of which Company Flat Rate" value={formatMoney(trace.flatRateInsideLabour)} />
                    <TraceRow label="Base cost" value={formatMoney(trace.baseCost)} />
                    <TraceRow
                      label={trace.adminCapped ? 'Admin fee (capped)' : 'Admin fee'}
                      value={`${formatMoney(trace.adminFee)} from ${formatMoney(trace.adminUncapped)} at ${percentLabel(quote.inputs.adminPct)}`}
                    />
                    <TraceRow label="Contingency" value={`${formatMoney(trace.contingency)} at ${percentLabel(quote.inputs.contingencyPct)} of base`} />
                    <TraceRow label="Profit margin" value={`${formatMoney(trace.profitMargin)} at ${percentLabel(quote.inputs.profitPct)} of base only`} />
                    <TraceRow label="Customer price ex VAT" value={formatMoney(trace.customerPriceExVat)} />
                    <TraceRow label="VAT" value={formatMoney(trace.vatAmount)} />
                    <TraceRow label="Customer price inc VAT" value={formatMoney(trace.customerPriceIncVat)} />
                    <TraceRow label="Quote-screen profit" value={formatMoney(trace.quoteScreenProfit)} />
                    <TraceRow
                      label="Quote-screen profit per day"
                      value={trace.quoteScreenProfitPerDay == null
                        ? 'Project duration is blank on this quote'
                        : `${formatMoney(trace.quoteScreenProfitPerDay)} over ${trace.quoteScreenDayDivisor} ${trace.quoteScreenDayDivisor === 1 ? 'day' : 'days'}`}
                    />
                  </Stack>
                </Sheet>
                <Typography level="body-xs" textColor="neutral.500">
                  Keystone days: {formatDays(comparison.keystone.days)}. {comparison.keystone.source}. The quote screen rounds a fractional project duration up to the next whole day. Profit per Keystone day in the comparison uses the duration as stored.
                </Typography>
              </Stack>
            </Card>

            <Card variant="outlined">
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Stack spacing={0.25}>
                    <Typography level="title-md">Job inputs</Typography>
                    <Typography level="body-xs" textColor="neutral.600">
                      Stored line kinds drive PS1. The analysis bucket is how PS2 and PS3 read the same line. Changing a bucket here stays in this workspace.
                    </Typography>
                  </Stack>
                  {Object.keys(overrides).length > 0 && (
                    <Button size="sm" variant="outlined" onClick={() => setOverrides({})}>Reset buckets</Button>
                  )}
                </Stack>
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                  <Table sx={{ minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Stored kind</th>
                        <th>Qty</th>
                        <th>Net</th>
                        <th>Analysis bucket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.lines.map((row) => (
                        <tr key={row.line.id}>
                          <td>
                            <Typography level="body-sm">{row.line.name}</Typography>
                            {row.line.vatInclusive && (
                              <Typography level="body-xs" textColor="neutral.500">VAT inclusive cost, netted before additions</Typography>
                            )}
                          </td>
                          <td><Typography level="body-sm">{row.line.lineKind}</Typography></td>
                          <td>
                            <Typography level="body-sm">
                              {row.line.qty}{row.line.unit ? ` ${row.line.unit}` : ''}
                            </Typography>
                          </td>
                          <td><Typography level="body-sm">{formatMoney(row.net)}</Typography></td>
                          <td>
                            <Select
                              size="sm"
                              value={row.analysisBucket}
                              onChange={(_, value) => {
                                if (!value) return;
                                setOverrides((current) => ({ ...current, [row.line.id]: value as CostBucket }));
                              }}
                              sx={{ minWidth: 180 }}
                            >
                              {BUCKETS.map((bucket) => (
                                <Option key={bucket} value={bucket}>{BUCKET_LABELS[bucket]}</Option>
                              ))}
                            </Select>
                          </td>
                        </tr>
                      ))}
                      {comparison.lines.length === 0 && (
                        <tr>
                          <td colSpan={5}>
                            <Typography level="body-sm" textColor="neutral.500">This version has no lines.</Typography>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Sheet>
              </Stack>
            </Card>

            <Card variant="outlined">
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                  <Typography level="title-md">PS3 market / mood</Typography>
                  <Typography level="body-sm">{mood.toFixed(2)}</Typography>
                </Stack>
                <Typography level="body-xs" textColor="neutral.600">
                  Scales the required return, which is PS2 net job profit. 1.00 leaves the price on PS2. 1.10 asks for 10% more return. 0.90 accepts 10% less. Costs stay where PS2 put them.
                </Typography>
                <Slider
                  min={0.5}
                  max={1.5}
                  step={0.05}
                  value={mood}
                  onChange={(_, value) => setMood(Array.isArray(value) ? value[0] : value)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => Number(value).toFixed(2)}
                />
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-xs" textColor="neutral.500">Quieter · 0.50</Typography>
                  <Button size="sm" variant="outlined" onClick={() => setMood(1)}>Reset 1.00</Button>
                  <Typography level="body-xs" textColor="neutral.500">Busier · 1.50</Typography>
                </Stack>
              </Stack>
            </Card>

            <Stack spacing={1}>
              <Typography level="title-md">Same job, three structures</Typography>
              <ComparisonTable comparison={comparison} />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              {[comparison.ps1, comparison.ps2, comparison.ps3].map((result) => (
                <Card key={result.version} variant="soft" sx={{ flex: 1 }}>
                  <Stack spacing={0.75}>
                    <Typography level="title-sm">{result.label}</Typography>
                    <Chip size="sm" variant="outlined">{result.version}</Chip>
                    {result.notes.map((note) => (
                      <Typography key={note} level="body-xs" textColor="neutral.700">{note}</Typography>
                    ))}
                  </Stack>
                </Card>
              ))}
            </Stack>
            </>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

function PS1_PARAMETER_LINE(quote: LoadedQuote): string {
  const fees = quote.inputs;
  const vat = fees.vatEnabled ? `VAT ${percentLabel(fees.vatPct)}` : 'VAT off';
  const deposit = !fees.hasDeposit
    ? 'no deposit'
    : fees.depositPercentage != null
      ? `deposit ${percentLabel(fees.depositPercentage)} of the VAT-inclusive total`
      : `fixed deposit ${formatMoney(fees.depositAmount)}`;
  return `Stored PS1 parameters: admin ${percentLabel(fees.adminPct)} capped at ${formatMoney(fees.adminCap)}, contingency ${percentLabel(fees.contingencyPct)}, profit margin ${percentLabel(fees.profitPct)} of base, ${vat}, ${deposit}.`;
}

function TraceRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography level="body-sm">{label}</Typography>
      <Typography level="body-sm" fontWeight="lg" textAlign="right">{value}</Typography>
    </Stack>
  );
}

export default PricingEnginePage;
